/**
 * appearance-chooser.js — wires up the "Appearance" section in user
 * settings (issue #252) and property branding (issue #691).
 *
 * User sections write luboo-prefs via themePrefs.
 * Property sections (`data-scope="property"`) PUT branding on the property.
 *
 * Markup contract (settings.html):
 *   .appearance-segmented[data-pref="theme|density|radius"] > button[data-value="..."]
 *   .appearance-swatches[data-pref="accent"]               > button[data-value="<hex or empty>"]
 *   button[data-action="appearance:reset"]
 *
 * The script:
 *   - Document-level click delegation: works whether the section is in the
 *     live DOM at load time or mounted later by uri-router. (The settings
 *     view lives inside <template id="views-template"> until uri-router
 *     clones it into a pane; binding a listener to the section at init
 *     time would silently miss the cloned copy.)
 *   - On any button click whose closest .appearance-section is set, calls
 *     themePrefs.set(<group's data-pref>, <button's data-value>).
 *   - Empty data-value on the accent reset swatch clears the override (null).
 *   - Refreshes [data-active] markers on every change AND every time a new
 *     .appearance-section is added to the DOM (uri-router remount).
 *
 * Listening to the storage event keeps multiple settings tabs in lockstep.
 */

import { lubooRequest } from '../mixins/data-channel.mixin.js';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Curated page tokens. Add a row here to expose another var(--token). */
const TOKEN_GROUPS = [
  {
    label: 'Surfaces',
    tokens: [
      { var: '--bg-color', label: 'Background' },
      { var: '--header-color', label: 'Sidebar / header' },
      { var: '--panel-color', label: 'Panel' },
      { var: '--panel-header', label: 'Panel header' },
      { var: '--active-tab', label: 'Active tab' },
    ],
  },
  {
    label: 'Text',
    tokens: [
      { var: '--text-color', label: 'Primary' },
      { var: '--text-hover', label: 'Hover' },
    ],
  },
  {
    label: 'Accent',
    tokens: [
      { var: '--highlight-color', label: 'Accent', swatches: true },
      { var: '--selection-bg', label: 'Selection' },
      { var: '--selection-text', label: 'Accent contrast' },
    ],
  },
  {
    label: 'Borders',
    tokens: [
      { var: '--border-color', label: 'Border' },
      { var: '--separator-color', label: 'Separator' },
    ],
  },
];

const ACCENT_SWATCHES = [
  '#7C5CFF', '#6B8FA3', '#22C55E', '#EAB308', '#E17B4C', '#F472B6', '#3498DB',
];

function prefsForSection(section) {
  if (section?.dataset.scope === 'property') {
    return window.LUBOO_CURRENT_PROPERTY?.branding || {};
  }
  return window.themePrefs ? window.themePrefs.get() : {};
}

function clonePalette(src) {
  if (!src || typeof src !== 'object') return null;
  const next = {};
  Object.entries(src).forEach(([key, value]) => {
    if (!value) return;
    if (key === 'background' || key === 'accent' || key.startsWith('--')) {
      next[key] = value;
    }
  });
  return Object.keys(next).length ? next : null;
}

function mergePalette(existing, patch) {
  if (patch === null) return null;
  if (patch === undefined) return clonePalette(existing);
  const merged = { ...(existing || {}), ...patch };
  const cleaned = {};
  Object.entries(merged).forEach(([key, value]) => {
    if (!value) return;
    if (key === 'background' || key === 'accent' || key.startsWith('--')) {
      cleaned[key] = value;
    }
  });
  return Object.keys(cleaned).length ? cleaned : null;
}

function resolvedEditorMode(prefs) {
  const theme = prefs?.theme;
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function rgbToHex(raw) {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (HEX_RE.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase();
  }
  const m = trimmed.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return '';
  const h = (n) => Number(n).toString(16).padStart(2, '0');
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
}

function computedToken(cssVar) {
  if (typeof document === 'undefined') return '';
  return rgbToHex(getComputedStyle(document.body).getPropertyValue(cssVar));
}

function paletteTokenValue(palette, cssVar) {
  const src = palette || {};
  if (src[cssVar]) return src[cssVar];
  if (cssVar === '--bg-color' && src.background) return src.background;
  if (cssVar === '--highlight-color' && src.accent) return src.accent;
  return '';
}

function ensureTokenEditor(section) {
  const host = section.querySelector('[data-role="token-rows"]');
  if (!host || host.childElementCount) return host;
  TOKEN_GROUPS.forEach((group) => {
    const wrap = document.createElement('div');
    wrap.className = 'token-group';
    const title = document.createElement('h4');
    title.className = 'token-group-title';
    title.textContent = group.label;
    wrap.appendChild(title);
    group.tokens.forEach((token) => {
      const row = document.createElement('div');
      row.className = 'token-row';
      row.dataset.cssVar = token.var;
      row.innerHTML = `
        <label class="token-row-label">
          <span>${token.label}</span>
          <code>${token.var}</code>
        </label>
        <div class="token-control">
          <span class="token-swatch">
            <input type="color" data-css-var="${token.var}" aria-label="${token.label} color">
          </span>
          <input type="text" data-css-var="${token.var}" maxlength="7" spellcheck="false" autocomplete="off" aria-label="${token.label} hex">
          <button type="button" class="appearance-swatch reset" data-action="token:clear" data-css-var="${token.var}" title="Use theme default" aria-label="Reset ${token.label}">×</button>
        </div>
      `;
      wrap.appendChild(row);
      if (token.swatches) {
        const swatchRow = document.createElement('div');
        swatchRow.className = 'token-swatches appearance-swatches';
        swatchRow.innerHTML = ACCENT_SWATCHES.map((hex) => (
          `<button type="button" class="appearance-swatch" data-css-var="--highlight-color" data-value="${hex}" style="background:${hex}" title="${hex}" aria-label="Accent ${hex}"></button>`
        )).join('');
        wrap.appendChild(swatchRow);
      }
    });
    host.appendChild(wrap);
  });
  return host;
}

function refreshTokenEditor(section, prefs, readonly) {
  const editor = section.querySelector('[data-token-editor]');
  if (!editor) return;
  ensureTokenEditor(section);
  const mode = resolvedEditorMode(prefs);
  editor.dataset.editMode = mode;
  const palette = prefs[mode] || {};
  editor.querySelectorAll('.token-row').forEach((row) => {
    const cssVar = row.dataset.cssVar;
    const override = paletteTokenValue(palette, cssVar);
    const value = override || computedToken(cssVar) || '#000000';
    const hex = value.startsWith('#') ? value : `#${value}`;
    const swatch = row.querySelector('.token-swatch');
    if (swatch) swatch.style.background = hex;
    row.querySelectorAll(`[data-css-var="${cssVar}"]`).forEach((el) => {
      if (el.matches('input[type="color"]') || el.matches('input[type="text"]')) {
        el.value = hex;
        el.disabled = readonly;
      }
    });
    if (override) row.setAttribute('data-overridden', '');
    else row.removeAttribute('data-overridden');
  });
  const accent = (paletteTokenValue(palette, '--highlight-color') || '').toLowerCase();
  editor.querySelectorAll('.token-swatches .appearance-swatch[data-value]').forEach((btn) => {
    if (accent && btn.dataset.value.toLowerCase() === accent) btn.setAttribute('data-active', '');
    else btn.removeAttribute('data-active');
  });
}

function refreshAll() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.appearance-section').forEach((section) => {
    const prefs = prefsForSection(section);
    const readonly = section.hasAttribute('data-readonly');
    section.querySelectorAll('[data-pref]').forEach((group) => {
      const pref = group.dataset.pref;
      const current = prefs[pref];
      group.querySelectorAll('button[data-value]').forEach((btn) => {
        const matches =
          (current == null || current === '')
            ? (btn.dataset.value === '')
            : btn.dataset.value === String(current);
        if (matches) btn.setAttribute('data-active', '');
        else btn.removeAttribute('data-active');
      });
    });
    refreshTokenEditor(section, prefs, readonly);
  });
}

export function brandingPayload(branding, patch) {
  const src = branding && typeof branding === 'object' ? branding : {};
  const nextPatch = patch && typeof patch === 'object' ? patch : {};
  const { light: lightPatch, dark: darkPatch, ...rest } = nextPatch;
  return {
    theme: src.theme ?? null,
    accent: src.accent ?? null,
    density: src.density ?? null,
    radius: src.radius ?? null,
    show_name: src.show_name !== false,
    light: mergePalette(src.light, lightPatch),
    dark: mergePalette(src.dark, darkPatch),
    ...rest,
  };
}

let _appearanceSave = Promise.resolve();

async function savePropertyAppearance(patch) {
  const run = async () => {
    const prop = window.LUBOO_CURRENT_PROPERTY;
    if (!prop?.id) return;
    const branding = brandingPayload(prop.branding, patch);
    const updated = await lubooRequest(
      `luboo:property:${prop.id}`,
      'update',
      { propertyId: prop.id, body: { branding } },
      { group: 'property', timeout: 15000 },
    );
    if (updated?.branding) {
      prop.branding = updated.branding;
    } else {
      prop.branding = branding;
    }
    window.dispatchEvent(new CustomEvent('luboo:property-branding-changed'));
  };
  const queued = _appearanceSave.then(run, run);
  _appearanceSave = queued.catch(() => {});
  return queued;
}

const _paletteSaveTimers = new Map();

function normalizeHex(raw) {
  if (raw == null) return null;
  let value = String(raw).trim();
  if (!value) return null;
  if (value[0] !== '#') value = `#${value}`;
  if (value.length === 4 && /^#[0-9A-Fa-f]{3}$/.test(value)) {
    value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return HEX_RE.test(value) ? value : null;
}

function syncTokenRow(row, value) {
  if (!row) return;
  const swatch = row.querySelector('.token-swatch');
  if (swatch) swatch.style.background = value || '';
  row.querySelectorAll('input[data-css-var]').forEach((el) => {
    el.value = value || el.value;
  });
  if (value) row.setAttribute('data-overridden', '');
  else row.removeAttribute('data-overridden');
}

function tokenPatch(cssVar, value) {
  const patch = { [cssVar]: value };
  if (cssVar === '--bg-color') patch.background = value;
  if (cssVar === '--highlight-color') patch.accent = value;
  return patch;
}

function previewAndSaveToken(mode, cssVar, value) {
  const prop = window.LUBOO_CURRENT_PROPERTY;
  if (!prop) return;
  const patch = tokenPatch(cssVar, value);
  prop.branding = brandingPayload(prop.branding, { [mode]: patch });
  window.dispatchEvent(new CustomEvent('luboo:property-branding-changed'));
  const key = `${mode}:${cssVar}`;
  const existing = _paletteSaveTimers.get(key);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    _paletteSaveTimers.delete(key);
    savePropertyAppearance({ [mode]: patch }).then(refreshAll).catch((err) => {
      console.warn('[appearance-chooser] token save failed', err);
    });
  }, 280);
  _paletteSaveTimers.set(key, timer);
}

function editorModeFromSection(section) {
  const editor = section.querySelector('[data-token-editor]');
  if (editor?.dataset.editMode === 'dark' || editor?.dataset.editMode === 'light') {
    return editor.dataset.editMode;
  }
  return resolvedEditorMode(prefsForSection(section));
}

function onTokenField(e) {
  const section = e.target.closest('.appearance-section[data-scope="property"]');
  if (!section || section.hasAttribute('data-readonly')) return;
  if (!e.target.closest('[data-token-editor]')) return;
  const field = e.target.closest('input[data-css-var]');
  if (!field) return;
  const hex = normalizeHex(field.value);
  if (!hex) {
    if (e.type === 'input' && field.matches('input[type="text"]')) return;
    return;
  }
  const row = field.closest('.token-row');
  syncTokenRow(row, hex);
  previewAndSaveToken(editorModeFromSection(section), field.dataset.cssVar, hex);
}

function init() {
  if (typeof document === 'undefined') return;
  if (!window.themePrefs) {
    console.warn('[appearance-chooser] themePrefs not available; bundle issue?');
    return;
  }
  if (window.__lubooAppearanceChooserBound) return;
  window.__lubooAppearanceChooserBound = true;

  document.addEventListener('change', onTokenField);
  document.addEventListener('input', onTokenField);

  // Document-level click delegation — survives uri-router view remounts.
  document.addEventListener('click', (e) => {
    const section = e.target.closest('.appearance-section');
    if (!section) return;
    if (section.hasAttribute('data-readonly')) return;

    const resetBtn = e.target.closest('[data-action="appearance:reset"]');
    if (resetBtn) {
      if (section.dataset.scope === 'property') {
        savePropertyAppearance({
          theme: null,
          accent: null,
          density: null,
          radius: null,
          light: null,
          dark: null,
        }).then(refreshAll).catch((err) => {
          console.warn('[appearance-chooser] property reset failed', err);
        });
        return;
      }
      window.themePrefs.reset();
      refreshAll();
      return;
    }

    const tokenEditor = e.target.closest('[data-token-editor]');
    if (section.dataset.scope === 'property' && tokenEditor) {
      const clearBtn = e.target.closest('[data-action="token:clear"]');
      if (clearBtn) {
        previewAndSaveToken(editorModeFromSection(section), clearBtn.dataset.cssVar, null);
        refreshAll();
        return;
      }
      const swatch = e.target.closest('button[data-value][data-css-var]');
      if (swatch) {
        const value = swatch.dataset.value === '' ? null : swatch.dataset.value;
        previewAndSaveToken(editorModeFromSection(section), swatch.dataset.cssVar, value);
        refreshAll();
        return;
      }
    }

    const btn = e.target.closest('button[data-value]');
    if (!btn) return;
    const group = btn.closest('[data-pref]');
    if (!group) return;
    const pref = group.dataset.pref;
    let value = btn.dataset.value;
    // accent reset swatch sends empty string; convert to null
    if (pref === 'accent' && value === '') value = null;
    if (section.dataset.scope === 'property') {
      const prop = window.LUBOO_CURRENT_PROPERTY;
      if (prop) {
        prop.branding = brandingPayload(prop.branding, { [pref]: value });
        window.dispatchEvent(new CustomEvent('luboo:property-branding-changed'));
      }
      savePropertyAppearance({ [pref]: value }).then(refreshAll).catch((err) => {
        console.warn('[appearance-chooser] property save failed', err);
      });
      refreshAll();
      return;
    }
    window.themePrefs.set(pref, value);
    refreshAll();
  });

  // Cross-tab / cross-pane sync via the storage event from another tab
  window.addEventListener('storage', (e) => {
    if (e.key === 'luboo-prefs') refreshAll();
  });
  window.addEventListener('luboo:property-branding-changed', refreshAll);

  // Re-paint active markers whenever uri-router clones the settings view
  // into a pane (the .appearance-section gets created fresh each mount).
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('.appearance-section') || node.querySelector?.('.appearance-section')) {
          refreshAll();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  refreshAll();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  // Kept for callers that want to force a re-paint after a manual mount.
  window.__lubooAppearanceChooserInit = init;
}
